import type CartAddedItemInfo from "@/interfaces/CartAddedItemInfo";

// Obtener el ID de Google Analytics y Google Tag Manager
const googleAnalyticsId = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
const googleTagManagerId = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID;
const googleAnalyticsEnabled = import.meta.env.VITE_GOOGLE_ANALYTICS_ENABLED;

export interface DataLayerItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  promotion_name?: string;
  price: number;
  quantity: number;
  discount?: number;
}

export interface DataLayerEcommerce {
  currency: string;
  value: number;
  items?: DataLayerItem[];
}

export interface DataLayerEvent {
  ecommerce: DataLayerEcommerce | null;
  event?: string;
}

export class GoogleAnalyticsService {
  public async init() {
    if (!googleAnalyticsEnabled) {
      return;
    }
    // Agregar el script de Google Analytics
    const gaScript = document.createElement("script");
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
    const gaInlineScript = document.createElement("script");
    gaInlineScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${googleAnalyticsId}');
    `;
    document.head.prepend(gaInlineScript);
    document.head.prepend(gaScript);

    // Agregar el script de Google Tag Manager
    const gtmScript = document.createElement("script");
    gtmScript.textContent = `
      (function(w,d,s,l,i){
        w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${googleTagManagerId}');
    `;
    document.head.appendChild(gtmScript);

    // Agregar el noscript de Google Tag Manager
    const gtmNoscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    gtmNoscript.appendChild(iframe);
    document.body.appendChild(gtmNoscript);

    return new Promise((resolve, reject) => {
      gaScript.onload = () => resolve(null);
      gaScript.onerror = () => reject();
    });
  }

  private initialized = false;
  public async initOnce() {
    if (this.initialized || !googleAnalyticsEnabled) {
      return;
    }
    this.initialized = true;
    await this.init();
  }

  public getDataLayer(): DataLayerEvent[] {
    const windowFixed = window as unknown as { dataLayer: DataLayerEvent[] };
    const dataLayer = windowFixed.dataLayer;
    return dataLayer;
  }

  public async pushEvent() {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const windowFixed = window as unknown as { dataLayer: DataLayerEvent[] };
    const dataLayer = windowFixed.dataLayer;
    if (!dataLayer) {
      throw "No se pudo agregar el evento analytics porque no se ha cargado el objeto window.dataLayer para Google Analytics.";
    }
    windowFixed.dataLayer.push({ ecommerce: null });
  }

  public async pushAddToCartEvent(addedInfo: CartAddedItemInfo) {
    if (!googleAnalyticsEnabled) {
      return;
    }
    await this.initOnce();
    const dlItems: DataLayerItem[] = [];
    if (addedInfo.common_quantity != 0) {
      dlItems.push({
        item_id: addedInfo.part_number || "DIG-" + addedInfo.product_id,
        item_name: addedInfo.product_name,
        item_brand: addedInfo.brand_name,
        item_category: addedInfo.category_name,
        price: addedInfo.common_price_with_tax,
        quantity: Math.abs(addedInfo.common_quantity),
      });
    }
    if (addedInfo.promotion_quantity != 0) {
      dlItems.push({
        item_id: addedInfo.part_number || "DIG-" + addedInfo.product_id,
        item_name: addedInfo.product_name,
        item_brand: addedInfo.brand_name,
        item_category: addedInfo.category_name,
        promotion_name: addedInfo.promotion_name,
        price: addedInfo.promotion_price_with_tax,
        quantity: Math.abs(addedInfo.promotion_quantity),
        discount:
          (addedInfo.common_price_with_tax -
            addedInfo.promotion_price_with_tax) *
          addedInfo.promotion_quantity,
      });
    }
    this.getDataLayer().push({ ecommerce: null });
    const event: DataLayerEvent = {
      event: addedInfo.total_quantity > 0 ? "add_to_cart" : "remove_from_cart",
      ecommerce: {
        currency: "MXN",
        value: addedInfo.total_with_tax,
        items: dlItems,
      },
    };
    this.getDataLayer().push(event);
    console.log(event);
  }
}

const googleAnalytics = new GoogleAnalyticsService();

export default googleAnalytics;
